import React, { useState, useRef } from 'react';
import { Upload, Sparkles, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import { Card } from 'shared/components/ui/Card';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { processImageForUpload, getExtensionFromMimeType } from 'utils/imageProcessing';
import { URLS } from 'configs';
import {
  getPrepUploadUrl,
  uploadToS3,
  analyzeFashionImage,
  formatApiError,
  formatRateLimitInfo,
} from 'api/fashionAnalysisApi';
import './UploadSection.scss';

// Image imports for demo
import inspirationImg from 'assets/landing/lookfinder/inspiration.jpg';
import blazerImg from 'assets/landing/lookfinder/blazer.jpg';
import topImg from 'assets/landing/lookfinder/top.jpg';
import pantsImg from 'assets/landing/lookfinder/pants.jpg';
import shoesImg from 'assets/landing/lookfinder/shoes.jpg';

const UploadSection = () => {
  const { t } = useTranslation();
  const alert = useAlert();
  const fileInputRef = useRef(null);

  // State management
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [isRateLimitError, setIsRateLimitError] = useState(false);
  const [loadingStage, setLoadingStage] = useState(null); // 'uploading', 'analyzing', 'searching'
  const [progress, setProgress] = useState(0);

  // Demo data (fallback)
  const demoLookItems = [
    {
      id: 1,
      name: 'Cream Blazer',
      price: 129.0,
      stores: 5,
      image: blazerImg,
    },
    {
      id: 2,
      name: 'White Tank Top',
      price: 25.0,
      stores: 8,
      image: topImg,
    },
    {
      id: 3,
      name: 'Rose Pleated Pants',
      price: 89.0,
      stores: 4,
      image: pantsImg,
    },
    {
      id: 4,
      name: 'Cream Leather Loafers',
      price: 159.0,
      stores: 3,
      image: shoesImg,
    },
  ];

  // Use API results or fallback to demo data
  const displayImage = uploadedImage || inspirationImg;
  const totalPrice = analysisResult
    ? analysisResult.matchGroups?.reduce((sum, group) => {
        // Handle both new schema (single match) and old schema (matches array)
        const match = group.match || group.matches?.[0];
        let price = 0;
        if (match?.price) {
          // Handle both { value: "SEK 76*", extracted_value: 76 } and "€129.00" formats
          if (typeof match.price === 'object' && match.price.extracted_value) {
            price = match.price.extracted_value;
          } else if (typeof match.price === 'string') {
            price = parseFloat(match.price.replace(/[^0-9.]/g, '')) || 0;
          }
        }
        return sum + price;
      }, 0) || 0
    : demoLookItems.reduce((sum, item) => sum + item.price, 0);

  // Handle file upload
  const handleFileUpload = async file => {
    if (!file) return;

    setError(null);
    setIsUploading(true);
    setLoadingStage('uploading');
    setProgress(0);

    try {
      // Step 1: Validate and optimize image (client-side)
      setProgress(10);
      const { blob, mimeType } = await processImageForUpload(file);

      // Create preview URL using FileReader (CSP-safe)
      const reader = new FileReader();
      reader.onload = e => {
        setUploadedImage(e.target.result);
      };
      reader.readAsDataURL(blob);

      // Step 2: Get file extension for API
      setProgress(20);
      const fileExtension = getExtensionFromMimeType(mimeType);

      // Simulate upload progress
      setProgress(30);
      await new Promise(resolve => setTimeout(resolve, 300));

      // Step 3: Get presigned upload URL
      setProgress(40);
      const { uploadUrl, fileId, contentType } = await getPrepUploadUrl(fileExtension);

      // Step 4: Upload optimized image to S3
      setProgress(50);
      await uploadToS3(uploadUrl, blob, contentType);

      setProgress(70);
      setLoadingStage('analyzing');
      await new Promise(resolve => setTimeout(resolve, 400));

      // Step 5: Analyze image using fileId
      setProgress(80);
      setIsAnalyzing(true);
      const result = await analyzeFashionImage(fileId);

      setProgress(90);
      setLoadingStage('searching');
      await new Promise(resolve => setTimeout(resolve, 500));

      setProgress(100);
      setAnalysisResult(result);
      setRateLimitInfo(result.limits || result.rateLimits);

      alert.success(
        t('upload_success', {
          count: result.totalMatchesDisplayed || result.itemsDetected || 0,
        }),
      );
    } catch (err) {
      // Reset to default photo on error
      setUploadedImage(null);
      setAnalysisResult(null);

      setError(formatApiError(err));
      setIsRateLimitError(err.status === 429 && err.limitType === 'per_day');

      // Show special alert for rate limit errors
      if (err.status === 429 && err.limitType === 'per_day') {
        alert.error(
          'Daily limit reached. Create a free account to get unlimited searches and save your favorite items.',
        );
      } else {
        alert.error(formatApiError(err));
      }

      if (err.rateLimits || err.limits) {
        setRateLimitInfo(err.rateLimits || err.limits);
      }
    } finally {
      setIsUploading(false);
      setIsAnalyzing(false);
      setLoadingStage(null);
      setProgress(0);
    }
  };

  // Handle file input change
  const handleFileChange = e => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Handle drag and drop
  const handleDragOver = e => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Handle click to upload
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Reset to demo
  const handleReset = () => {
    setUploadedImage(null);
    setAnalysisResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <section className='upload-section'>
      {/* Background Elements */}
      <div className='upload-section__bg-wrapper'>
        <div className='upload-section__bg-element upload-section__bg-element--1' aria-hidden='true' />
        <div className='upload-section__bg-element upload-section__bg-element--2' aria-hidden='true' />
        <div className='upload-section__bg-element upload-section__bg-element--3' aria-hidden='true' />
      </div>

      <div className='upload-section__container'>
        <div className='upload-section__header'>
          <div className='upload-section__badge'>
            <Sparkles className='upload-section__badge-icon' aria-hidden='true' />
            <span className='upload-section__badge-text'>{t('upload_badge', 'AI-Powered')}</span>
          </div>
          <h2 className='upload-section__title'>{t('upload_title_1', 'From Inspiration to Purchase')}</h2>
          <p className='upload-section__subtitle'>
            {t('upload_subtitle', 'Turn any fashion inspiration into a complete shoppable outfit')}
          </p>
        </div>

        {/* Demo Section */}
        <div className='upload-section__demo'>
          <Card className='upload-section__demo-card'>
            <div className='upload-section__demo-content'>
              {/* Left Side - Uploaded Photo */}
              <div className='upload-section__demo-left'>
                <div className='upload-section__demo-label'>
                  <Upload className='upload-section__demo-label-icon' size={16} />
                  <span>{uploadedImage ? 'Your Photo' : 'Demo Photo'}</span>
                  {uploadedImage && (
                    <button onClick={handleReset} className='upload-section__reset-btn' title='Reset to demo'>
                      ✕
                    </button>
                  )}
                </div>
                <div
                  className={`upload-section__demo-image-wrapper ${isDragging ? 'is-dragging' : ''} ${
                    isUploading || isAnalyzing ? 'is-loading' : ''
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={!isUploading && !isAnalyzing ? handleUploadClick : undefined}
                  style={{ cursor: isUploading || isAnalyzing ? 'default' : 'pointer' }}
                >
                  <img src={displayImage} alt='Fashion inspiration' className='upload-section__demo-image' />

                  {/* Upload overlay */}
                  {!uploadedImage && !isUploading && !isAnalyzing && (
                    <div className='upload-section__upload-overlay'>
                      <Upload size={32} />
                      <p>Click or drag to upload your fashion photo</p>
                    </div>
                  )}

                  {/* Loading overlay */}
                  {(isUploading || isAnalyzing) && (
                    <div className='upload-section__loading-overlay'>
                      <div className='upload-section__loading-content'>
                        <Loader2 size={48} className='spinning' />

                        {/* Progress bar */}
                        <div className='upload-section__progress-bar'>
                          <div className='upload-section__progress-fill' style={{ width: `${progress}%` }} />
                        </div>

                        {/* Stage message */}
                        <div className='upload-section__loading-stage'>
                          {loadingStage === 'uploading' && (
                            <>
                              <strong>{t('upload_stage_uploading', 'Uploading...')}</strong>
                              <span>{t('upload_stage_uploading_desc', 'Preparing your image')}</span>
                            </>
                          )}
                          {loadingStage === 'analyzing' && (
                            <>
                              <strong>{t('upload_stage_analyzing', 'AI Analyzing...')}</strong>
                              <span>{t('upload_stage_analyzing_desc', 'Detecting fashion items')}</span>
                            </>
                          )}
                          {loadingStage === 'searching' && (
                            <>
                              <strong>{t('upload_stage_searching', 'Finding Matches...')}</strong>
                              <span>{t('upload_stage_searching_desc', 'Searching across stores')}</span>
                            </>
                          )}
                        </div>

                        {/* Progress percentage */}
                        <div className='upload-section__progress-percent'>{progress}%</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type='file'
                  accept='image/*'
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />

                {/* Rate limit info */}
                {rateLimitInfo && (
                  <div className='upload-section__rate-limit'>
                    <small>{formatRateLimitInfo(rateLimitInfo)}</small>
                  </div>
                )}

                {/* Error message */}
                {error && (
                  <div
                    className={`upload-section__error ${isRateLimitError ? 'upload-section__error--rate-limit' : ''}`}
                  >
                    <AlertCircle size={16} />
                    <div className='upload-section__error-content'>
                      <span>{error}</span>
                      {isRateLimitError && (
                        <a href={URLS.CONTACT} className='upload-section__error-cta'>
                          {t('contact_us', 'Contact us')}
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Side - Shop Items */}
              <div className='upload-section__demo-right'>
                <div className='upload-section__demo-label'>
                  <Sparkles className='upload-section__demo-label-icon' size={16} />
                  <span>Shop This Look</span>
                  {analysisResult && (
                    <span className='upload-section__items-count'>
                      {analysisResult.itemsDetected} {analysisResult.itemsDetected === 1 ? 'item' : 'items'} detected
                    </span>
                  )}
                </div>

                <div className='upload-section__demo-items'>
                  {analysisResult
                    ? // Display API results
                      analysisResult.matchGroups?.map((group, idx) => {
                        // Handle both new schema (single match) and old schema (matches array)
                        const match = group.match || group.matches?.[0];
                        return match ? (
                          <div key={idx} className='upload-section__demo-item'>
                            <div className='upload-section__demo-item-image'>
                              <img src={match.thumbnail} alt={match.title} />
                              <span className='upload-section__item-category'>
                                {group.category || group.fashionItem?.category || 'Item'}
                              </span>
                            </div>
                            <div className='upload-section__demo-item-info'>
                              <div className='upload-section__demo-item-name'>{match.title}</div>
                              <div className='upload-section__demo-item-price'>
                                {match.price?.value || match.price || 'Price unavailable'}
                              </div>
                              <div className='upload-section__demo-item-stores'>
                                {group.totalMatchesFound || group.displayedCount || group.matchCount || 0}{' '}
                                {group.totalMatchesFound === 1 ? 'match' : 'matches'} found
                              </div>
                            </div>
                            <a
                              href={match.link}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='upload-section__demo-item-link'
                            >
                              View Product
                              <ExternalLink size={14} />
                            </a>
                          </div>
                        ) : null;
                      })
                    : // Display demo data
                      demoLookItems.map(item => (
                        <div key={item.id} className='upload-section__demo-item'>
                          <div className='upload-section__demo-item-image'>
                            <img src={item.image} alt={item.name} />
                          </div>
                          <div className='upload-section__demo-item-info'>
                            <div className='upload-section__demo-item-name'>{item.name}</div>
                            <div className='upload-section__demo-item-price'>${item.price.toFixed(2)}</div>
                            <div className='upload-section__demo-item-stores'>Available at {item.stores} stores</div>
                          </div>
                          <button className='upload-section__demo-item-link'>
                            View Product
                            <ExternalLink size={14} />
                          </button>
                        </div>
                      ))}
                </div>

                <div className='upload-section__demo-total'>
                  <span className='upload-section__demo-total-label'>Total Look:</span>
                  <span className='upload-section__demo-total-price'>
                    {analysisResult && totalPrice > 0 ? `$${totalPrice.toFixed(2)}` : `$${totalPrice.toFixed(2)}`}
                  </span>
                </div>

                {analysisResult && (
                  <div className='upload-section__analysis-info'>
                    <small>
                      Analysis completed in {(analysisResult.processingTime / 1000).toFixed(1)}s •{' '}
                      {analysisResult.totalMatchesFound} total matches
                    </small>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Bottom Steps */}
        <div className='upload-section__steps'>
          <div className='upload-section__step-item'>
            <div className='upload-section__step-number'>1</div>
            <h3 className='upload-section__step-title'>{t('upload_step_1_title', 'Upload')}</h3>
            <p className='upload-section__step-description'>
              {t('upload_step_1_desc', 'Drop any fashion photo or paste product URL from any online store')}
            </p>
          </div>

          <div className='upload-section__step-item'>
            <div className='upload-section__step-number'>2</div>
            <h3 className='upload-section__step-title'>{t('upload_step_2_title', 'AI Analyzes')}</h3>
            <p className='upload-section__step-description'>
              {t('upload_step_2_desc', 'AI identifies every clothing item, accessory, and footwear piece')}
            </p>
          </div>

          <div className='upload-section__step-item'>
            <div className='upload-section__step-number'>3</div>
            <h3 className='upload-section__step-title'>{t('upload_step_3_title', 'Shop')}</h3>
            <p className='upload-section__step-description'>
              {t('upload_step_3_desc', 'Get shoppable links with prices from multiple stores')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UploadSection;
